/**
 * The ticket-picker's per-type ceiling, and which source can honestly answer it.
 *
 * The system narrows PROGRESSIVELY, and each tier is the correct granularity for the question
 * being asked at that point — not a degraded stand-in for an answer that never arrives:
 *
 * | user has selected | the question is                       | correct source                  |
 * |-------------------|---------------------------------------|---------------------------------|
 * | nothing yet       | "how many could I book at all?"       | date-level `ticketsAvailability`|
 * | a date            | "...on this date?"                    | that date's slots               |
 * | a date and a time | "...in this slot?"                    | that slot                       |
 *
 * The two slot-derived tiers need no distinction here: they both read `validTimeslots`, which is
 * already filtered by the selected date AND the selected time. So the only question this module
 * answers is "can the slot-level path answer yet?".
 *
 * This replaced a schema-presence check on both domain stores:
 *
 *     if (!dates?.some((d) => d.ticketsAvailability)) return null   // "did the response include it?"
 *
 * which asked whether the BE returned the field. That is true on every normal response, so the
 * slot-level branch was UNREACHABLE in production rather than merely deprioritised — the ceiling
 * never advanced past tier 1 even once a date and a time were selected and a genuinely more
 * discriminative answer was sitting right there. It also conflated an absent field with
 * no-data-yet, since `dates === []` during first load took the same branch.
 *
 * The tightening as tiers advance is the CORRECTION, not a regression: each tier can only ever be
 * narrower than the one before, the same optimistic->authoritative shape the engine already
 * implements for the seed/quantity-scoped entry pair. Note this does NOT clamp a selection already
 * made — pick 8 while times are in flight, settle to 5, and the stored 8 is kept. That gap is
 * backstopped at checkout (the order fails and `invalidateAvailability` hard-refetches) and is
 * deliberately out of scope here.
 */

/**
 * Whether the slot-level answer can be computed yet.
 *
 * `timesLoading` is the gate because it means the selected date has NO slots at all — neither from
 * its times entry nor from the dates seed covering its week — so `validTimeslots` is empty and the
 * slot-derived number would be a misleading `0`. That is what makes the date-level answer a genuine
 * placeholder in that window rather than a worse answer. It deliberately does NOT mean "the
 * authoritative times fetch is still in flight": while the seed's slots are on screen that is
 * usable slot data, and the slot-level path should already win.
 */
export function hasSlotLevelAnswer({
  selectedDate,
  timesLoading
}: {
  selectedDate?: string
  timesLoading: boolean
}): boolean {
  return !!selectedDate && !timesLoading
}

/**
 * Every date-level `ticketsAvailability` entry for one ticket, across the dates passed in. Returns
 * an empty array when the field is absent — callers reduce it to a max or a boolean, and both
 * treat "no entries" as zero, so absent-field and zero-spots deliberately converge here. Which of
 * the two it was is no longer a routing decision (that is `hasSlotLevelAnswer`'s job), which is
 * exactly what the old `| null` return existed to express.
 */
export function ticketsAvailabilityFor(
  dates: AvailabilityDate[] | null | undefined,
  ticketId: string
): TicketDateAvailability[] {
  return (dates ?? []).flatMap(
    (date) => date.ticketsAvailability?.filter((t) => t.resourceOptionId === ticketId) ?? []
  )
}

// Highest date-level `spotsAvailable` for one ticket — the tier-1 ceiling.
export function dateLevelMaxFor(dates: AvailabilityDate[] | null | undefined, ticketId: string): number {
  return ticketsAvailabilityFor(dates, ticketId).reduce((max, t) => Math.max(max, t.spotsAvailable), 0)
}

// Whether any date carries spare capacity for one ticket — the tier-1 availability signal.
export function dateLevelHasSpotsFor(dates: AvailabilityDate[] | null | undefined, ticketId: string): boolean {
  return ticketsAvailabilityFor(dates, ticketId).some((t) => t.spotsAvailable > 0)
}
